import React, { useMemo, useState } from "react";
import HighlightMatch from "./HighlightMatch";
import ReactCountryFlag from "react-country-flag";

import Icon from "../components/ui/Icon";
import Money from "../components/ui/Money";
import formatMessageTime from "./formatMessageTime";
import matchSnippet from "./matchSnippet";
import pricesOf, { isProcessing } from "./pricesOf";
import whatsappChatLink from "./whatsappChatLink";
import { useWhatsappTarget } from "./whatsappTarget";
import "../stock/components/core-list-card.scss";
import "./tf-card.scss";

/**
 * One raw WhatsApp message, as a Stock Alert card (Alex, 2026-10-20: "make the
 * cards on Trade feed similar to the cards on Stock alert page").
 *
 * Built on Stock Alert's own frame (`cl-card`, stock/components/
 * core-list-card.scss), as the Deals page is, with the old card's glass
 * surfaces. ONE size whatever the message holds: every row has a fixed height
 * and the photo is a square, so a card with a price, a pill or a forwarded
 * chip is exactly as big as one without (the old card grew a row for its
 * pills, and the pills clipped). From top to bottom:
 *
 *   - flag, sender, group and time, as on the old card (Alex, 2026-10-20);
 *   - the photo, with the status pills on it where Stock Alert puts "at
 *     target". No green border: the pill already says it. No photo leaves
 *     the box blank;
 *   - the message, three lines (it opens the popup, as the photo does);
 *   - the price band: the approved trade price, else the price the pipeline
 *     extracted (labelled Unapproved), else "No price";
 *   - the number and WhatsApp.
 *
 * Every price here is MINOR units, like every product_prices figure.
 */

/**
 * The price line: the dealer's own figure and, when it differs, the GBP
 * conversion. A message quoting several watches shows the first and a count;
 * for EXTRACTED prices `count` is the server's total (it sends only a few) and
 * the first is the watch matching the search.
 */
export const TradePrice = ({ prices, count = prices.length }) => {
  const [first] = prices;
  // This price's own status (Alex, 2026-10-23): a list can mix approved and
  // unapproved prices, so the pills on the photo, which describe the whole
  // message, cannot say which one this is.
  const status = first.approved ? "Approved trade price" : "Unapproved: read by the pipeline, not yet approved";
  const converted = first.currency !== "gbp" && first.price_gbp != null;
  const more = count - 1;
  // Two lines, always (Alex, 2026-10-23): the watch and its price on the
  // first, the £ conversion and how many other prices on the second. Either
  // part of the second line may be absent (a GBP price, a one-watch message);
  // the line keeps its height regardless, so every card stays the same size.
  // Line 1: the watch, its price and the £ figure. Line 2: how many other
  // prices the message has, as a small chip, or nothing (Alex, 2026-10-23).
  // Line 2 keeps its height either way, so every card is the same size.
  return (
    <span className="tf-card__price" title={[status, first.reference, first.name].filter(Boolean).join(" · ")}>
      <span className="tf-card__price-line">
        <span
          className={`tf-card__price-status${first.approved ? " is-approved" : ""}`}
          role="img"
          aria-label={first.approved ? "Approved" : "Unapproved"}
        />
        {/* Which watch this price is. The server puts a searched reference
            first and marks it, so a 116503 search shows the 116503 price. */}
        {first.reference && (
          <>
            <span className={`tf-card__price-ref${first.match ? " is-match" : ""}`}>{first.reference}</span>
            <span className="tf-card__price-dash" aria-hidden="true">–</span>
          </>
        )}
        <span className="tf-card__price-main">
          <Money value={first.price} currency={first.currency} decimals={0} />
        </span>
        {converted && (
          <span className="tf-card__price-conv">
            ≈ <Money value={first.price_gbp} currency="gbp" decimals={0} tone="muted" />
          </span>
        )}
      </span>
      <span className="tf-card__price-line">
        {more > 0 && (
          <span className="tf-card__price-more">
            +{more} more {more === 1 ? "price" : "prices"}
          </span>
        )}
      </span>
    </span>
  );
};



const SenderFlag = ({ country }) => {
  if (!country) {
    return (
      <span className="tf-card__flag tf-card__flag--unknown" aria-hidden="true">
        <Icon name="Globe" size="xs" />
      </span>
    );
  }
  return (
    <span className="tf-card__flag">
      <ReactCountryFlag
        countryCode={country}
        svg
        aria-label={country}
        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
      />
    </span>
  );
};

const Media = ({ item, fallbackImage }) => {
  // An S3 URL is presigned for 30 minutes and this page is built to be left
  // open, so an image CAN stop loading under a long-lived tab. A broken-image
  // icon in a grid of two dozen cards reads as "the page is broken"; this says
  // what actually happened. A reload re-signs every URL.
  const [failed, setFailed] = useState(false);

  if (item.media_kind === "image" && item.media_url && !failed) {
    return (
      <img
        src={item.media_url}
        alt={item.file_name || "WhatsApp attachment"}
        // These are third-party photos on a page that shows dozens at a time;
        // lazy keeps a fast scroll from opening 24 S3 connections at once.
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  if (failed) {
    return (
      <span className="tf-card__no-media">
        <Icon name="Image" size="md" />
        <span>Image link expired — reload</span>
      </span>
    );
  }

  if (item.media_url) {
    // Video and documents are not previewed inline — the popup opens them.
    return (
      <span className="tf-card__file">
        <Icon name={item.media_kind === "video" ? "Camera" : "Paperclip"} size="md" />
        <span className="tf-card__file-name">{item.file_name || "Attachment"}</span>
      </span>
    );
  }

  if (item.media_kind) {
    // The message HAS an attachment but we have no copy of it: the S3 upload in
    // WhatsappService is best-effort and the upstream WhatsApp URL expires, so
    // this is a real production state. Saying so beats rendering it as a plain
    // text message, which would silently drop the fact that a photo existed.
    return (
      <span className="tf-card__no-media">
        <Icon name="Image" size="md" />
        <span>{item.media_kind === "image" ? "Image" : "Attachment"} unavailable</span>
      </span>
    );
  }

  // A TEXT message with no attachment of its own. On Trade Feed that is the
  // end of it. On Core List the card is about a watch we can identify, so the
  // caller may offer the CATALOGUE photo — labelled, never passed off as the
  // dealer's own picture, because "here is a photo of the watch" and "here is
  // the photo they sent" are different claims.
  if (fallbackImage) {
    return (
      <>
        <img src={fallbackImage} alt="" loading="lazy" />
        <span className="tf-card__stock-photo">Catalogue photo</span>
      </>
    );
  }

  return <span className="tf-card__no-media">Text message</span>;
};

/**
 * @param {node} extra  Optional price block used INSTEAD of the card's own
 *   price band. Core List's matches pass the price it measured against the
 *   target; the feed passes nothing. The card is reused there rather than
 *   copied, so a fix to one is a fix to both.
 */
const TradeFeedCard = ({ item, onOpen, query = "", extra = null, fallbackImage = null }) => {
  const time = formatMessageTime(item.timestamp);

  // On a forty-line price list the match is rarely in the opening lines, so
  // show the lines around it instead. Null when there is no search or no
  // literal match, which is the whole of the no-search behaviour unchanged.
  const snippet = useMemo(
    () => matchSnippet(item.message_text, query),
    [item.message_text, query]
  );

  const price = pricesOf(item);
  // The chat with a reply typed: WhatsApp Web on a computer, the app on a
  // phone (see whatsappChatLink).
  const waTarget = useWhatsappTarget();
  const waLink = whatsappChatLink(item, waTarget);
  const others = (item.forwarded_to ?? []).slice(1);
  const hasMedia = Boolean(item.media_kind || item.media_url || fallbackImage);
  const text = item.message_text ? <HighlightMatch text={snippet ?? item.message_text} query={query} /> : null;

  const open = () => onOpen(item);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  // The pills describe the MESSAGE: "Trade price added" = at least one of
  // its prices is approved; "Unapproved" = it has prices and none is approved
  // yet. Which price is which is on the price row and in the popup.
  const onlyUnapproved = price.kind !== "none" && price.approvedCount === 0 && !item.badges?.trade_price_added;
  const pills = (item.badges?.trade_price_added || item.badges?.lead_present || (onlyUnapproved && !extra)) && (
    <span className="cl-card__found">
      {item.badges?.trade_price_added && <span className="cl-card__found-trade">Trade price added</span>}
      {onlyUnapproved && !extra && <span className="cl-card__found-missed">Unapproved</span>}
      {item.badges?.lead_present && <span className="tf-card__found-lead">Lead</span>}
    </span>
  );

  return (
    // No green "found" border (Alex, 2026-10-20): the pill already says it.
    <article className="cl-card tf-card" aria-label={`Message from ${item.sender_name}`}>
      {/* Who, where and when, above the photo as on the old card (Alex,
          2026-10-20). Fixed height: two lines whatever the names. */}
      <header className="tf-card__head">
        <SenderFlag country={item.sender_country} />
        <span className="tf-card__who">
          <span className="tf-card__sender" title={item.sender_name}>
            <HighlightMatch text={item.sender_name} query={query} />
          </span>
          <span className="tf-card__group" title={[item.group_name || "Direct message", ...others].join("\n")}>
            <span className="tf-card__group-name">
              {item.group_name ? <HighlightMatch text={item.group_name} query={query} /> : "Direct message"}
            </span>
            {others.length > 0 && <span className="tf-card__forwards">+{others.length}</span>}
          </span>
        </span>
        <time className="tf-card__time" dateTime={item.timestamp_iso || undefined} title={time.absolute}>
          {time.relative}
        </time>
      </header>

      {/* The whole photo opens the popup (Alex, 2026-10-16): it is the
          biggest target on the card and the obvious thing to click. A
          message with no photo leaves the box blank (Alex, 2026-10-20): the
          text stays in its own row, so every card reads the same way. */}
      <div className="cl-card__media tf-card__media" role="button" tabIndex={0} aria-label="Open message" onClick={open} onKeyDown={onKey}>
        {hasMedia ? <Media item={item} fallbackImage={fallbackImage} /> : null}
        {pills}
      </div>

      {/* The message, under the photo: three lines. The whole row opens the
          popup; the eye icon that sat here is gone (Alex, 2026-10-21). */}
      <div className="tf-card__text" onClick={open}>
        <p className="tf-card__text-body">{text ?? <em>No text — attachment only</em>}</p>
      </div>

      <div className={`tf-card__prices${extra ? " tf-card__prices--extra" : ""}`}>
        {extra ??
          (price.kind === "none" ? (
            <span className="tf-card__price">
              <span className={`tf-card__price-line tf-card__no-price${isProcessing(item) ? " is-processing" : ""}`}>
                {isProcessing(item) ? "Processing" : "No price"}
              </span>
              <span className="tf-card__price-line" aria-hidden="true" />
            </span>
          ) : (
            <TradePrice prices={price.prices} count={price.count} />
          ))}
      </div>

      <div className="cl-card__foot">
        {/* No number means no chat to open, so no WhatsApp button: saying so
            once beats a button that silently does nothing. */}
        <span className="tf-card__phone">{item.sender_phone || "No number"}</span>
        <span className="cl-card__actions">
          {waLink ? (
            // The old card's outlined pill, no fill (Alex, 2026-10-20).
            <a
              className="tf-card__wa"
              href={waLink.href}
              {...(waLink.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              <img src="/assets/icons/whatsapp-green-circle.svg" alt="" width="16" height="16" aria-hidden="true" />
              WhatsApp
            </a>
          ) : null}
        </span>
      </div>
    </article>
  );
};

export default TradeFeedCard;
